import { useSyncExternalStore } from 'react'
import type { OverlayId, OverlayPropsById, OverlayResult } from './registry'
import { preloadEntry } from './registry'

/**
 * The overlay stack: every dialog, drawer and confirm open in the app, bottom
 * first (#209, ADR-0019 D6). The one record of what is open — the host renders
 * it, nothing else keeps its own.
 *
 * - `open(id, props)` replaces the whole stack: the palette, a row's confirm,
 *   a drawer from a page. Two unrelated panels never fight over focus.
 * - `push(id, props)` opens on top of what is open and returns a promise of
 *   the overlay's result: a drawer asks another drawer for something (Add
 *   signals → a new cookie) and gets the answer back, without losing its own
 *   state underneath.
 * - `close(key, result?)` closes that entry, and any it pushed, settling each
 *   promise exactly once — `undefined` for anything but a result handed over
 *   (Cancel, Escape, the backdrop, being replaced).
 *
 * An entry is `{id, props, key}`: `id` and `props` are what the registry types
 * and what a link could carry (plain data, for every overlay a link may open);
 * `key` is this store's own, a fresh number per open, the content's React key.
 *
 * State only, a few hundred bytes in the eager bundle. The overlay CODE lives
 * behind the registry (registry.tsx) and loads the first time it opens. Props
 * are snapshot at open time and the host keeps rendering an entry through its
 * exit animation, so a confirm's title never blanks while it fades out.
 */
export interface OverlayEntry<K extends OverlayId = OverlayId> {
  id: K
  props: OverlayPropsById[K]
  /** A fresh number on every open: the content's React key, and the handle `close` takes. */
  key: number
}

const EMPTY: readonly OverlayEntry[] = Object.freeze([])

let stack: readonly OverlayEntry[] = EMPTY
let nonce = 0
/** Each open entry's promise, settled once and forgotten. */
const settlers = new Map<number, (result: unknown) => void>()
/** Where focus goes back to once an entry has gone: what held it when the entry opened. */
const openers = new Map<number, HTMLElement | null>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const cb of listeners) cb()
}

function focused(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  return document.activeElement instanceof HTMLElement ? document.activeElement : null
}

function settle(entries: readonly OverlayEntry[], result?: (key: number) => unknown): void {
  for (const e of entries) {
    const resolve = settlers.get(e.key)
    settlers.delete(e.key)
    resolve?.(result?.(e.key))
  }
}

/** `open(id)` for an overlay whose props are all optional, `open(id, props)` otherwise. */
type PropsArg<K extends OverlayId> =
  Partial<OverlayPropsById[K]> extends OverlayPropsById[K]
    ? [props?: OverlayPropsById[K]]
    : [props: OverlayPropsById[K]]

function entry<K extends OverlayId>(id: K, props: OverlayPropsById[K] | undefined): OverlayEntry {
  nonce += 1
  return { id, props: (props ?? {}) as OverlayPropsById[K], key: nonce } as OverlayEntry
}

/**
 * Replaces everything open with this overlay. Focus is recorded now, not later:
 * content that autofocuses has moved it by the time any effect runs. A
 * replacement keeps the first opener, so closing it returns to where the reader
 * started.
 */
function open<K extends OverlayId>(id: K, ...[props]: PropsArg<K>): void {
  const next = entry(id, props)
  const first = stack[0]
  openers.set(next.key, first ? (openers.get(first.key) ?? null) : focused())
  for (const e of stack) openers.delete(e.key)
  const replaced = stack
  stack = [next]
  settle(replaced)
  emit()
}

/**
 * Opens this overlay on top of what is open and resolves with its result —
 * what its content passed to `close(result)` — or `undefined` if it was left
 * any other way. Focus goes back to what held it now, inside the overlay
 * underneath, once this one has gone.
 */
function push<K extends OverlayId>(
  id: K,
  ...[props]: PropsArg<K>
): Promise<OverlayResult<K> | undefined> {
  const next = entry(id, props)
  openers.set(next.key, focused())
  const answer = new Promise<OverlayResult<K> | undefined>((resolve) => {
    settlers.set(next.key, resolve as (result: unknown) => void)
  })
  stack = [...stack, next]
  emit()
  return answer
}

/**
 * Closes one entry — the top when no `key` is given — and every entry pushed
 * on top of it; `result` settles that entry's promise, the ones above settle
 * with `undefined`. A key no longer open is a no-op: content that closes itself
 * after an async save must not close whatever replaced it meanwhile.
 */
function close(key?: number, result?: unknown): void {
  const at = key === undefined ? stack.length - 1 : stack.findIndex((e) => e.key === key)
  if (at < 0) return
  const target = stack[at].key
  const gone = stack.slice(at)
  stack = at === 0 ? EMPTY : stack.slice(0, at)
  settle(gone, (k) => (k === target ? result : undefined))
  emit()
}

/** The palette's ⌘K: closes it when it is on top, else opens it over nothing. */
function toggle(id: OverlayId): void {
  if (stack.at(-1)?.id === id) close()
  else open(id as never)
}

/** Start fetching an overlay's chunk ahead of opening it. Safe to call often. */
function preload(id: OverlayId): void {
  preloadEntry(id)
}

export const overlays = { open, push, close, toggle, preload }

/**
 * Where focus goes once this entry has left the DOM; consumed on read.
 * `undefined`: nowhere — it was replaced, and its replacement holds focus now.
 */
export function takeOpener(key: number): HTMLElement | null | undefined {
  if (!openers.has(key)) return undefined
  const el = openers.get(key) ?? null
  openers.delete(key)
  return el
}

/** Everything open, bottom first. The same array until something opens or closes. */
export function getOverlays(): readonly OverlayEntry[] {
  return stack
}

/** The top entry, or null. */
export function getOverlay(): OverlayEntry | null {
  return stack.at(-1) ?? null
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** Everything open, bottom first. */
export function useOverlays(): readonly OverlayEntry[] {
  return useSyncExternalStore(subscribe, getOverlays, () => EMPTY)
}

/**
 * Props to spread on whatever opens an overlay: hovering or focusing it starts
 * fetching the overlay's chunk, so it is usually in cache by the click.
 */
export function useOpener(id: OverlayId): { onPointerEnter: () => void; onFocus: () => void } {
  return { onPointerEnter: () => preload(id), onFocus: () => preload(id) }
}

/** Closes everything with no result and forgets every opener: a page leaving, a test ending. */
export function resetOverlays(): void {
  const gone = stack
  stack = EMPTY
  openers.clear()
  settle(gone)
  emit()
}
